import type { ReadResult } from 'zxing-wasm/reader';

export interface ScanCandidate {
  readonly value: string;
  readonly format: ReadResult['format'];
  readonly count: number;
  readonly firstSeenAt: number;
  readonly lastSeenAt: number;
}

export interface ScanObservation {
  readonly confirmed: boolean;
  readonly candidate?: ScanCandidate;
}

export class ScanConfirmationTracker {
  private readonly candidatesByValue = new Map<string, ScanCandidate>();

  constructor(
    private readonly requiredDetections = 2,
    private readonly confirmationWindowMs = 1_000,
  ) {}

  observe(value: string, format: ReadResult['format'], detectedAt: number): ScanObservation {
    const previous = this.candidatesByValue.get(value);
    const withinWindow = previous !== undefined
      && detectedAt - previous.firstSeenAt <= this.confirmationWindowMs;
    const candidate: ScanCandidate = withinWindow
      ? {
          value,
          format,
          count: previous.count + 1,
          firstSeenAt: previous.firstSeenAt,
          lastSeenAt: detectedAt,
        }
      : {
          value,
          format,
          count: 1,
          firstSeenAt: detectedAt,
          lastSeenAt: detectedAt,
        };

    if (candidate.count >= this.requiredDetections) {
      this.candidatesByValue.delete(value);
      return { confirmed: true };
    }

    this.candidatesByValue.set(value, candidate);
    return { confirmed: false, candidate };
  }

  list(activeAt: number): readonly ScanCandidate[] {
    this.prune(activeAt);
    return [...this.candidatesByValue.values()].sort((left, right) => right.lastSeenAt - left.lastSeenAt);
  }

  clear(): void {
    this.candidatesByValue.clear();
  }

  forget(value: string): void {
    this.candidatesByValue.delete(value);
  }

  private prune(activeAt: number): void {
    for (const [value, candidate] of this.candidatesByValue) {
      if (activeAt - candidate.firstSeenAt > this.confirmationWindowMs) {
        this.candidatesByValue.delete(value);
      }
    }
  }
}
